/**
 * The wire: what the league has just done, in one line at a time.
 *
 * DERIVED EVENTS ONLY, AND THAT IS A PERMISSION FACT RATHER THAN A PREFERENCE. The obvious source
 * is `seasons/{season}/audit`, which already carries who did what — and `firestore.rules` gates
 * that collection to admin reads, deliberately, because commissioners are the people the log is
 * most often evidence about. A feed built on it would render empty for nine of the ten managers
 * and nobody would find out until draft night.
 *
 * So the wire reads the records themselves: submitted slates, FAAB lines, filed trades, picks and
 * proposals. Every one of those is readable by any signed-in account and every one carries a name
 * and a time. What it therefore cannot show is sign-ins, page views, deletions and access
 * decisions, which are exactly the four things `auditLog.ts` has to store because they leave no
 * record to derive. That is a real gap and it is the right gap: those four are an admin's
 * business, and the wire is the league's.
 *
 * COALESCING IS NOT COSMETIC. A commissioner entering a season's FAAB backfill writes twenty rows
 * in two minutes, and a fifteen-line feed showing twenty of them shows nothing else that happened
 * all week. Same actor, same kind, inside `COALESCE_MS` collapses to one line that says how many.
 * The same thing happens without a commissioner: ten slates land in the last hour before the
 * keeper deadline, and the wire is more useful saying so than listing them.
 */

import { managerShort } from './identity.js';

export type WireKind = 'slate' | 'faab' | 'trade' | 'pick' | 'proposal' | 'docket';

export interface WireLine {
  key: string;
  /** ISO. The feed is newest first. */
  at: string;
  kind: WireKind;
  /** Who did it, uppercase GM id, or null where the event belongs to the league. */
  gm: string | null;
  /** The whole line, already worded. */
  text: string;
  /** How many events this line stands for. 1 unless it coalesced. */
  count: number;
  /** Where tapping it goes. */
  to: [section: string, page: string];
}

/** Same actor, same kind, inside this window is one line. Ten minutes covers a sitting. */
export const COALESCE_MS = 10 * 60 * 1000;

/** The ticker's length: how many lines rotate on Home. The header does not count it aloud. */
export const WIRE_LENGTH = 15;

/**
 * The feed's length: what the sheet shows when the ticker is tapped (Tim, 3 September: "a list
 * of all the 50 most recent items on the wire and THOSE would deeplink me to the relevant area").
 * The ticker is the first fifteen of these, so the two can never disagree about order.
 */
export const WIRE_FEED_LENGTH = 50;

export interface WireInput {
  /** Submitted declarations only. A draft slate is private to its manager. */
  declarations: ReadonlyArray<{
    gm: string; status: string; submittedAt?: string; updatedAt: string;
    /**
     * MEN KEPT, NOT SLOTS USED. A franchise tag eats three slots, so a four-man slate with one
     * tag on it uses six — and the wire saying "6 kept" is wrong in the way nobody forgives,
     * because every reader can count the four names on the card next to it. Tax is priced on
     * slots and is reported as a figure, which needs no unit; the count is reported as men.
     */
    kept?: number;
    /** Tax owed, already assessed by the caller. */
    tax?: number;
  }>;
  faab: ReadonlyArray<{ id: string; gm: string; amount: number; description: string; date: string }>;
  trades: ReadonlyArray<{ id: string; sides: ReadonlyArray<{ gm: string }>; filedAt: string }>;
  picks: ReadonlyArray<{
    overall: number; round: number; pick: number; player?: string; madeAt?: string;
    madeBy?: string; gm: string; isKeeper: boolean;
  }>;
  proposals: ReadonlyArray<{
    id: string; gm: string; title: string; status: string; createdAt: string; updatedAt: string;
  }>;
}

const faabAmount = (n: number): string =>
  `${n < 0 ? '−' : '+'}₣${Math.abs(n)}`;

/**
 * Every event the league can read, unsorted.
 *
 * A KEEPER PICK IS NOT AN EVENT. It was placed on the board by the declaration that bought it,
 * weeks earlier, and reporting it on draft night as though somebody had just taken him would put
 * sixty lines of old news on the wire in the first minute of the draft.
 */
function events(i: WireInput): WireLine[] {
  const out: WireLine[] = [];

  for (const d of i.declarations) {
    if (d.status !== 'submitted') continue;
    const at = d.submittedAt ?? d.updatedAt;
    if (!at) continue;
    const kept = d.kept === undefined ? '' : ` — ${d.kept} kept`;
    const tax = d.tax === undefined ? '' : `, tax ₣${d.tax}`;
    out.push({
      key: `slate:${d.gm}`,
      at,
      kind: 'slate',
      gm: d.gm.toUpperCase(),
      text: `${managerShort(d.gm)} submitted his slate${kept}${tax}`,
      count: 1,
      to: ['keepers', 'slate'],
    });
  }

  for (const e of i.faab) {
    out.push({
      key: `faab:${e.id}`,
      at: e.date,
      kind: 'faab',
      gm: e.gm.toUpperCase(),
      text: `${managerShort(e.gm)} — ${e.description} ${faabAmount(e.amount)}`,
      count: 1,
      to: ['league', 'faab'],
    });
  }

  for (const t of i.trades) {
    const [a, b] = [t.sides[0]?.gm, t.sides[1]?.gm];
    if (!a || !b) continue;
    out.push({
      key: `trade:${t.id}`,
      at: t.filedAt,
      kind: 'trade',
      /* A trade belongs to two people, so it belongs to neither for coalescing purposes. */
      gm: null,
      text: `${managerShort(a)} ⇄ ${managerShort(b)} trade recorded`,
      count: 1,
      to: ['league', 'trades'],
    });
  }

  for (const p of i.picks) {
    if (!p.player || p.isKeeper || !p.madeAt) continue;
    /*
     * THE MAN WHO HAS HIM, NOT THE MAN WHO TYPED HIM (Tim, 8 September). `took` is a claim about
     * a roster, and on a night when the commissioner enters picks for the room `madeBy` is the
     * commissioner ten times a round — so the feed read "Tim took CeeDee Lamb" over a pick that
     * is Ryan's, and filtering the feed by GM handed a man everybody else's picks. Who did the
     * typing is the audit log's question, and it answers it with `onBehalfOf` alongside.
     */
    const who = p.gm;
    out.push({
      key: `pick:${p.overall}`,
      at: p.madeAt,
      kind: 'pick',
      gm: who.toUpperCase(),
      text: `${managerShort(who)} took ${p.player} at ${p.round}.${String(p.pick).padStart(2, '0')}`,
      count: 1,
      to: ['draft', 'board'],
    });
  }

  for (const p of i.proposals) {
    /* A draft proposal is private to its author until he submits it. */
    if (p.status === 'draft' || p.status === 'withdrawn') continue;
    const decided = p.status === 'passed' || p.status === 'failed' || p.status === 'provisional';
    out.push({
      key: `prop:${p.id}`,
      at: decided ? p.updatedAt : p.createdAt,
      kind: decided ? 'docket' : 'proposal',
      gm: decided ? null : p.gm.toUpperCase(),
      text: decided
        ? `${p.title} — ${p.status}`
        : `${managerShort(p.gm)} submitted a rule proposal`,
      count: 1,
      to: ['league', 'proposals'],
    });
  }

  return out;
}

/** Plural forms for the coalesced line, one per kind. */
const MANY: Record<WireKind, (n: number) => string> = {
  slate: (n) => `${n} keeper slates submitted`,
  faab: (n) => `${n} FAAB movements`,
  trade: (n) => `${n} trades recorded`,
  pick: (n) => `${n} picks made`,
  proposal: (n) => `${n} rule proposals submitted`,
  docket: (n) => `${n} docket items decided`,
};

/**
 * The feed: newest first, coalesced, capped.
 *
 * Coalescing walks the sorted list once and folds an event into the line above it when the actor
 * and the kind match and the gap is under the window. The FOLDED line keeps the newest timestamp,
 * because that is when the run finished and that is the time a reader is comparing against.
 */
export function wire(input: WireInput, limit = WIRE_LENGTH): WireLine[] {
  const all = events(input)
    .filter((e) => e.at && !Number.isNaN(Date.parse(e.at)))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  const out: WireLine[] = [];
  for (const e of all) {
    const prev = out[out.length - 1];
    /*
     * A TRADE NEVER FOLDS (Tim, 3 September). Every other kind coalesces on a shared actor, and
     * a trade has none — it belongs to two men, so `gm` is null and two unrelated deals filed in
     * the same ten minutes matched each other and became "2 trades recorded", which is the one
     * line on the wire that people actually stop and read. Each deal gets its own.
     */
    const sameRun = prev
      && prev.kind === e.kind
      && e.kind !== 'trade'
      && prev.gm === e.gm
      && Date.parse(prev.at) - Date.parse(e.at) < COALESCE_MS;
    if (sameRun && prev) {
      prev.count += 1;
      const many = MANY[prev.kind](prev.count);
      prev.text = prev.gm ? `${managerShort(prev.gm)} recorded ${many}` : many;
      continue;
    }
    out.push({ ...e });
    if (out.length >= limit) break;
  }
  return out;
}
